import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SelectItemProps } from '@ui/src';
import { AccountType, Total, ReportSheet, Category } from '../../types';
import { Safeguarding } from '../types';
import { safeguardingApi } from './services';
import formReport from '../../helpers/form-report';

export interface SafeguardingState {
  safeguarding?: ReportSheet[];
  itemsVisible: AccountType[];
  formationTime?: {
    start?: number;
    end?: number;
    duration?: number;
  };
  formationTimeCrypto?: {
    start?: number;
    end?: number;
    duration?: number;
  };
  lists: {
    categories: SelectItemProps[];
    groups: SelectItemProps[];
    primaries: SelectItemProps[];
  };
  totalAssets?: Total;
  totalLiabilities?: Total;
  checksum?: number;
  excess?: number;
  accountsCategories?: Category[];
  safeguardingCrypto?: ReportSheet[];
  excessCrypto?: number;
}

const initialState: SafeguardingState = {
  safeguarding: undefined,
  itemsVisible: [],
  formationTime: undefined,
  lists: {
    categories: [],
    groups: [],
    primaries: [],
  },
  checksum: 0,
  excess: 0,
  excessCrypto: 0,
  safeguardingCrypto: undefined,
};

export const safeguardingSlice = createSlice({
  name: 'safeguarding',
  initialState,
  reducers: {
    changeVisibility: (state, action: PayloadAction<AccountType[]>) => {
      state.itemsVisible = action.payload;
    },
    setSafeguardingAccountsCategories: (
      state,
      action: PayloadAction<Category[]>,
    ) => {
      state.accountsCategories = action.payload;
    },
    clearSafeguardingState: (state) => {
      state.safeguarding = undefined;
      state.itemsVisible = [];
      state.checksum = 0;
      state.totalAssets = undefined;
      state.totalLiabilities = undefined;
      state.excess = 0;
      state.excessCrypto = 0;
      state.safeguardingCrypto = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        safeguardingApi.endpoints.getSafeguardingReport.matchPending,
        (state) => {
          state.formationTime = {
            start: Date.now(),
            end: undefined,
            duration: undefined,
          };
        },
      )
      .addMatcher(
        safeguardingApi.endpoints.getSafeguardingCryptoReport.matchPending,
        (state) => {
          state.formationTimeCrypto = {
            start: Date.now(),
            end: undefined,
            duration: undefined,
          };
        },
      )
      .addMatcher(
        safeguardingApi.endpoints.getSafeguardingReport.matchFulfilled,
        (
          state,
          action: PayloadAction<{ data: Safeguarding }> & {
            meta: { fulfilledTimeStamp: number };
          },
        ) => {
          const { checksum, totalAssets, totalLiabilities, excess } =
            action.payload.data;

          const accountCategories = state.accountsCategories;

          const reportResult = formReport({
            ...action.payload.data,
            accountCategories: accountCategories as Category[],
          });

          state.lists = reportResult.lists;
          state.safeguarding = reportResult.sheet;

          state.checksum = checksum || 0;
          state.totalAssets = totalAssets || undefined;
          state.totalLiabilities = totalLiabilities || undefined;
          state.excess = excess;

          state.formationTime = {
            ...state.formationTime,
            end: action.meta.fulfilledTimeStamp,
            duration: Math.round(
              (action.meta.fulfilledTimeStamp -
                (state.formationTime?.start as number)) /
                1000,
            ),
          };
        },
      )
      .addMatcher(
        safeguardingApi.endpoints.getSafeguardingCryptoReport.matchFulfilled,
        (
          state,
          action: PayloadAction<{ data: Safeguarding }> & {
            meta: { fulfilledTimeStamp: number };
          },
        ) => {
          const { excess } = action.payload.data;

          const accountCategories = state.accountsCategories;

          const reportResult = formReport({
            ...action.payload.data,
            accountCategories: accountCategories as Category[],
          });

          state.lists = reportResult.lists;
          state.safeguardingCrypto = reportResult.sheet;

          state.excessCrypto = excess;

          state.formationTimeCrypto = {
            ...state.formationTime,
            end: action.meta.fulfilledTimeStamp,
            duration: Math.round(
              (action.meta.fulfilledTimeStamp -
                (state.formationTime?.start as number)) /
                1000,
            ),
          };
        },
      );
  },
});

export const {
  clearSafeguardingState,
  changeVisibility,
  setSafeguardingAccountsCategories,
} = safeguardingSlice.actions;

export default safeguardingSlice.reducer;
